/**
 * ShippingRateRuleCreatePage — Wizard-style creation page for Shipping Rate Rules.
 *
 * Guided wizard:
 *   - Apply On picker (All Items / Item Code / Item Group / Collection)
 *   - Calculate As config (Fixed Per Item / Fixed Per Order / Percentage / Free)
 *   - Conditions (optional: weight, amount, qty thresholds)
 *   - Free Shipping Threshold
 *   - Title & Priority
 *
 * Connects to Frappe via generic doc creation API.
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
// Switch import removed — not currently used
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import { useLanguage } from "@/i18n/useLanguage";
import { getERPClient } from "@/lib/erp/client";
import { CARD } from "@/config/design-tokens";
import {
  Check,
  Package,
  Layers,
  Grid3X3,
  Globe,
  Truck,
  DollarSign,
  Percent,
  Gift,
  Plus,
  X,
} from "lucide-react";

// ── Types ───────────────────────────────────────────────────

type ApplyOn = "All Items" | "Item Code" | "Item Group" | "Collection" | null;
type CalculateAs = "Fixed Per Item" | "Fixed Per Order" | "Percentage of Item Amount" | "Free" | null;

interface ConditionRow {
  condition_type: string;
  operator: string;
  value: string;
}

interface WizardState {
  applyOn: ApplyOn;
  scopeValue: string;
  calculateAs: CalculateAs;
  shippingAmount: string;
  freeShippingThreshold: string;
  title: string;
  priority: string;
  label: string;
  matchLogic: "All (AND)" | "Any (OR)";
  conditions: ConditionRow[];
}

const INITIAL: WizardState = {
  applyOn: null,
  scopeValue: "",
  calculateAs: null,
  shippingAmount: "",
  freeShippingThreshold: "",
  title: "",
  priority: "10",
  label: "",
  matchLogic: "All (AND)",
  conditions: [],
};

// ── i18n labels ─────────────────────────────────────────────

const labels: Record<string, { en: string; th: string }> = {
  page_title: { en: "New Shipping Rate Rule", th: "สร้างกฎค่าจัดส่งใหม่" },
  page_desc: { en: "Set shipping cost per item, category, or collection", th: "ตั้งค่าจัดส่งตามสินค้า หมวดหมู่ หรือคอลเลกชัน" },
  scope_header: { en: "Apply To", th: "ใช้กับ" },
  cost_header: { en: "Shipping Cost", th: "ค่าจัดส่ง" },
  conditions_header: { en: "Conditions (Optional)", th: "เงื่อนไข (ไม่บังคับ)" },
  settings_header: { en: "Settings", th: "ตั้งค่า" },
  create_btn: { en: "Create Shipping Rule", th: "สร้างกฎค่าจัดส่ง" },
  creating_btn: { en: "Creating...", th: "กำลังสร้าง..." },
  title_label: { en: "Rule Name", th: "ชื่อกฎ" },
  title_placeholder: { en: "e.g. Heavy Item Surcharge", th: "เช่น ค่าจัดส่งสินค้าหนัก" },
  priority_label: { en: "Priority", th: "ลำดับความสำคัญ" },
  priority_desc: { en: "Higher = checked first", th: "สูงกว่า = ตรวจสอบก่อน" },
  amount_label: { en: "Amount (฿)", th: "จำนวน (฿)" },
  pct_label: { en: "Percentage (%)", th: "เปอร์เซ็นต์ (%)" },
  free_threshold: { en: "Free above order total", th: "ฟรีเมื่อยอดสั่งซื้อเกิน" },
  free_threshold_desc: { en: "Leave empty to disable", th: "เว้นว่างเพื่อปิด" },
  display_label: { en: "Display label (customer sees)", th: "ข้อความแสดง (ลูกค้าเห็น)" },
  add_condition: { en: "Add Condition", th: "เพิ่มเงื่อนไข" },
};

// ── Apply On Picker ───────────────────────────────────���─────

function ApplyOnPicker({ value, onChange, lang }: {
  value: ApplyOn;
  onChange: (v: ApplyOn) => void;
  lang: string;
}) {
  const items: { type: ApplyOn; icon: typeof Package; label: { en: string; th: string }; desc: { en: string; th: string } }[] = [
    { type: "All Items", icon: Globe, label: { en: "All Items", th: "ทุกสินค้า" }, desc: { en: "Apply to every product", th: "ใช้กับสินค้าทั้งหมด" } },
    { type: "Item Code", icon: Package, label: { en: "Specific Item", th: "สินค้าเฉพาะ" }, desc: { en: "One specific product", th: "สินค้าตัวเดียว" } },
    { type: "Item Group", icon: Layers, label: { en: "Category", th: "หมวดหมู่" }, desc: { en: "All items in a group", th: "สินค้าในกลุ่มเดียวกัน" } },
    { type: "Collection", icon: Grid3X3, label: { en: "Collection", th: "คอลเลกชัน" }, desc: { en: "Storefront collection", th: "คอลเลกชันหน้าร้าน" } },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map((item) => {
        const active = value === item.type;
        const Icon = item.icon;
        return (
          <button key={item.type} type="button" onClick={() => onChange(item.type)}
            className={cn(
              "relative rounded-md p-4 transition-all text-left bg-white dark:bg-card",
              active ? "shadow-sm ring-1 ring-primary/30" : "hover:shadow-sm border border-border hover:border-muted-foreground/30"
            )}>
            <div className="space-y-2.5">
              <div className={cn("w-9 h-9 rounded flex items-center justify-center transition-colors", active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className={cn("text-sm font-medium", active ? "text-primary" : "text-foreground")}>
                  {lang === "th" ? item.label.th : item.label.en}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  {lang === "th" ? item.desc.th : item.desc.en}
                </p>
              </div>
            </div>
            {active && (
              <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Check className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Calculate As Picker ─────────────────────────────────────

function CalculateAsPicker({ value, onChange, lang }: {
  value: CalculateAs;
  onChange: (v: CalculateAs) => void;
  lang: string;
}) {
  const items: { type: CalculateAs; icon: typeof Truck; label: { en: string; th: string }; desc: { en: string; th: string } }[] = [
    { type: "Fixed Per Item", icon: DollarSign, label: { en: "Fixed Per Item", th: "คงที่ต่อชิ้น" }, desc: { en: "฿X × quantity", th: "฿X × จำนวน" } },
    { type: "Fixed Per Order", icon: Truck, label: { en: "Fixed Per Order", th: "คงที่ต่อออเดอร์" }, desc: { en: "Flat fee (once)", th: "ค่าคงที่ (ครั้งเดียว)" } },
    { type: "Percentage of Item Amount", icon: Percent, label: { en: "Percentage", th: "เปอร์เซ็นต์" }, desc: { en: "% of item amount", th: "% ของราคาสินค้า" } },
    { type: "Free", icon: Gift, label: { en: "Free Shipping", th: "จัดส่งฟรี" }, desc: { en: "No shipping cost", th: "ไม่มีค่าจัดส่ง" } },
  ];

  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map((item) => {
        const active = value === item.type;
        const Icon = item.icon;
        return (
          <button key={item.type} type="button" onClick={() => onChange(item.type)}
            className={cn(
              "relative rounded-md p-4 transition-all text-left bg-white dark:bg-card",
              active ? "shadow-sm ring-1 ring-primary/30" : "hover:shadow-sm border border-border hover:border-muted-foreground/30"
            )}>
            <div className="space-y-2.5">
              <div className={cn("w-9 h-9 rounded flex items-center justify-center transition-colors", active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className={cn("text-sm font-medium", active ? "text-primary" : "text-foreground")}>
                  {lang === "th" ? item.label.th : item.label.en}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  {lang === "th" ? item.desc.th : item.desc.en}
                </p>
              </div>
            </div>
            {active && (
              <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Check className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Condition Row ───────────────────────────────────────────

const CONDITION_TYPES = [
  { value: "Item Weight", label: { en: "Item Weight", th: "น้ำหนักสินค้า" } },
  { value: "Item Amount", label: { en: "Item Amount", th: "ราคาสินค้า" } },
  { value: "Item Qty", label: { en: "Item Quantity", th: "จำนวนสินค้า" } },
  { value: "Order Total", label: { en: "Order Total", th: "ยอดรวมออเดอร์" } },
  { value: "Order Weight", label: { en: "Order Weight", th: "น้ำหนักรวม" } },
  { value: "Order Qty", label: { en: "Order Quantity", th: "จำนวนรวม" } },
];

const OPERATORS = [">=", "<=", ">", "<", "="];

function ConditionRowEditor({ condition, onChange, onRemove, lang }: {
  condition: ConditionRow;
  onChange: (c: ConditionRow) => void;
  onRemove: () => void;
  lang: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <select value={condition.condition_type} onChange={(e) => onChange({ ...condition, condition_type: e.target.value })}
        className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm">
        {CONDITION_TYPES.map((ct) => (
          <option key={ct.value} value={ct.value}>{lang === "th" ? ct.label.th : ct.label.en}</option>
        ))}
      </select>
      <select value={condition.operator} onChange={(e) => onChange({ ...condition, operator: e.target.value })}
        className="w-16 rounded-md border border-border bg-background px-2 py-2 text-sm text-center">
        {OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
      </select>
      <Input type="number" value={condition.value} onChange={(e) => onChange({ ...condition, value: e.target.value })}
        className="w-24" placeholder="0" />
      <button type="button" onClick={onRemove} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ── Section Label ───────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{children}</h2>;
}

// ── Build payload ───────────────────────────────────────────

function buildPayload(s: WizardState): Record<string, unknown> {
  const data: Record<string, unknown> = {
    doctype: "Shipping Rate Rule",
    enabled: 1,
    title: s.title || "Shipping Rule",
    priority: parseInt(s.priority) || 10,
    apply_on: s.applyOn || "All Items",
    calculate_as: s.calculateAs || "Fixed Per Item",
    shipping_amount: s.calculateAs === "Free" ? 0 : parseFloat(s.shippingAmount) || 0,
    free_shipping_threshold: parseFloat(s.freeShippingThreshold) || 0,
    label: s.label || "",
    match_logic: s.matchLogic,
  };

  if (s.applyOn === "Item Code" && s.scopeValue) {
    data.item_code = s.scopeValue;
  } else if (s.applyOn === "Item Group" && s.scopeValue) {
    data.item_group = s.scopeValue;
  } else if (s.applyOn === "Collection" && s.scopeValue) {
    data.collection = s.scopeValue;
  }

  if (s.conditions.length > 0) {
    data.conditions = s.conditions
      .filter((c) => c.value)
      .map((c) => ({
        condition_type: c.condition_type,
        operator: c.operator,
        value: parseFloat(c.value) || 0,
      }));
  }

  return data;
}

// ── Main Component ──────────────────────────────────────────

export function ShippingRateRuleCreatePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [s, setS] = useState<WizardState>(INITIAL);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = (key: string) => {
    const entry = labels[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const set = useCallback(<K extends keyof WizardState>(k: K, v: WizardState[K]) => {
    setS((p) => ({ ...p, [k]: v }));
  }, []);

  const needsAmount = s.calculateAs && s.calculateAs !== "Free";

  // Progress steps
  const steps = [
    !!s.applyOn,
    !!s.calculateAs,
    s.calculateAs === "Free" || (!!s.shippingAmount && parseFloat(s.shippingAmount) > 0),
    !!s.title,
  ];
  const completed = steps.filter(Boolean).length;

  const canCreate = s.applyOn && s.calculateAs && s.title &&
    (s.calculateAs === "Free" || (parseFloat(s.shippingAmount) > 0)) &&
    (s.applyOn === "All Items" || !!s.scopeValue);

  const handleCreate = async () => {
    if (!canCreate) return;
    setIsSaving(true);
    setError(null);
    try {
      const client = getERPClient();
      const result = await client.create({ doctype: "Shipping Rate Rule", data: buildPayload(s) });
      navigate(`/inventory/shipping-rate-rule/${(result as any).name}`);
    } catch (err: any) {
      setError(err?.message || "Failed to create shipping rule");
      setIsSaving(false);
    }
  };

  const addCondition = () => {
    set("conditions", [...s.conditions, { condition_type: "Item Weight", operator: ">=", value: "" }]);
  };

  const updateCondition = (idx: number, c: ConditionRow) => {
    const next = [...s.conditions];
    next[idx] = c;
    set("conditions", next);
  };

  const removeCondition = (idx: number) => {
    set("conditions", s.conditions.filter((_, i) => i !== idx));
  };

  return (
    <div className="min-h-screen bg-background py-10 font-sans">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold">{t("page_title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("page_desc")}</p>
            </div>
            {s.applyOn && (
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  {steps.map((done, i) => (
                    <div key={i} className={cn("w-8 h-1.5 rounded-full transition-all duration-300", done ? "bg-primary" : "bg-muted")} />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground tabular-nums ml-1">{completed}/{steps.length}</span>
              </div>
            )}
          </div>
        </div>

        {/* Wizard sections */}
        <div className="space-y-8">
          {/* 1. Apply To */}
          <div className="space-y-3">
            <SectionLabel>{t("scope_header")}</SectionLabel>
            <ApplyOnPicker value={s.applyOn} onChange={(v) => { set("applyOn", v); set("scopeValue", ""); }} lang={language} />

            {/* Scope value selector */}
            {s.applyOn === "Item Code" && (
              <Card className={CARD}>
                <CardContent className="p-4">
                  <DocTypeSearchAutocomplete
                    doctype="Item"
                    value={s.scopeValue}
                    onChange={(v) => set("scopeValue", v)}
                    placeholder={language === "th" ? "เลือกสินค้า..." : "Select item..."}
                  />
                </CardContent>
              </Card>
            )}
            {s.applyOn === "Item Group" && (
              <Card className={CARD}>
                <CardContent className="p-4">
                  <DocTypeSearchAutocomplete
                    doctype="Item Group"
                    value={s.scopeValue}
                    onChange={(v) => set("scopeValue", v)}
                    placeholder={language === "th" ? "เลือกหมวดหมู่..." : "Select category..."}
                  />
                </CardContent>
              </Card>
            )}
            {s.applyOn === "Collection" && (
              <Card className={CARD}>
                <CardContent className="p-4">
                  <DocTypeSearchAutocomplete
                    doctype="Storefront Collection"
                    value={s.scopeValue}
                    onChange={(v) => set("scopeValue", v)}
                    placeholder={language === "th" ? "เลือกคอลเลกชัน..." : "Select collection..."}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* 2. Calculate As */}
          {s.applyOn && (
            <div className="space-y-3">
              <SectionLabel>{t("cost_header")}</SectionLabel>
              <CalculateAsPicker value={s.calculateAs} onChange={(v) => set("calculateAs", v)} lang={language} />

              {needsAmount && (
                <Card className={CARD}>
                  <CardContent className="p-5 space-y-4">
                    <div>
                      <label className="text-sm font-medium text-foreground">
                        {s.calculateAs === "Percentage of Item Amount" ? t("pct_label") : t("amount_label")}
                      </label>
                      <Input type="number" value={s.shippingAmount} onChange={(e) => set("shippingAmount", e.target.value)}
                        placeholder="0" className="mt-1.5 text-lg tabular-nums" />
                    </div>
                    <div className="border-t border-border/50 pt-4">
                      <label className="text-sm font-medium text-foreground">{t("free_threshold")}</label>
                      <p className="text-xs text-muted-foreground mt-0.5">{t("free_threshold_desc")}</p>
                      <Input type="number" value={s.freeShippingThreshold} onChange={(e) => set("freeShippingThreshold", e.target.value)}
                        placeholder="0" className="mt-1.5 tabular-nums" />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* 3. Conditions */}
          {s.calculateAs && (
            <div className="space-y-3">
              <SectionLabel>{t("conditions_header")}</SectionLabel>
              <Card className={CARD}>
                <CardContent className="p-5 space-y-3">
                  {s.conditions.length > 0 && (
                    <div className="space-y-2">
                      {s.conditions.length > 1 && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-muted-foreground">{language === "th" ? "ตรรกะ:" : "Logic:"}</span>
                          <button type="button" onClick={() => set("matchLogic", s.matchLogic === "All (AND)" ? "Any (OR)" : "All (AND)")}
                            className={cn("px-2.5 py-1 rounded text-xs font-medium transition-colors",
                              s.matchLogic === "All (AND)" ? "bg-primary/10 text-primary" : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                            )}>
                            {s.matchLogic === "All (AND)" ? "AND" : "OR"}
                          </button>
                        </div>
                      )}
                      {s.conditions.map((c, i) => (
                        <ConditionRowEditor key={i} condition={c} lang={language}
                          onChange={(updated) => updateCondition(i, updated)}
                          onRemove={() => removeCondition(i)} />
                      ))}
                    </div>
                  )}
                  <button type="button" onClick={addCondition}
                    className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium">
                    <Plus className="w-3.5 h-3.5" /> {t("add_condition")}
                  </button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* 4. Settings */}
          {s.calculateAs && (
            <div className="space-y-3">
              <SectionLabel>{t("settings_header")}</SectionLabel>
              <Card className={CARD}>
                <CardContent className="p-5 space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t("title_label")}</label>
                    <Input value={s.title} onChange={(e) => set("title", e.target.value)}
                      placeholder={t("title_placeholder")} className="mt-1.5" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground">{t("priority_label")}</label>
                      <p className="text-xs text-muted-foreground">{t("priority_desc")}</p>
                      <Input type="number" value={s.priority} onChange={(e) => set("priority", e.target.value)}
                        className="mt-1.5 tabular-nums" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground">{t("display_label")}</label>
                      <Input value={s.label} onChange={(e) => set("label", e.target.value)}
                        placeholder={language === "th" ? "เช่น ค่าส่งสินค้า��นัก" : "e.g. Heavy Item Shipping"}
                        className="mt-1.5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Create button */}
          {s.calculateAs && (
            <Button onClick={handleCreate} disabled={!canCreate || isSaving}
              className="w-full h-12 text-base font-medium" size="lg">
              <Truck className="w-4 h-4 mr-2" />
              {isSaving ? t("creating_btn") : t("create_btn")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
